"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import ReactFlagsSelect from "react-flags-select";
import { LanguagesIcon } from "lucide-react";

interface MobileCarouselProps {
  changeLanguageMob: (code: string) => void;
  currentLangMob: string;
}

const MainCarousel: React.FC<MobileCarouselProps> = ({ changeLanguageMob, currentLangMob }) => {
  const description = 'Our trusted hospital dedicated to delivering effective treatments by 1000 specialist doctors.';

  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [country, setCountry] = useState("")

  useEffect(() => {
    if (selectedLanguage === "LK") {
      changeLanguageMob("si")
    } else if (selectedLanguage === "US") {
      changeLanguageMob("en")
    } else if (selectedLanguage === "DE") {
      changeLanguageMob("de")
    }
  }, [selectedLanguage])

  useEffect(() => {
    if (currentLangMob === "si") {
      setCountry("LK")
    } else if (currentLangMob === "en") {
      setCountry("US")
    } else if (currentLangMob === "de") {
      setCountry("DE")
    }
  }, [currentLangMob])



  return (
    <div className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50 opacity-90 -z-10"></div>

      {/* Animated floating elements */}
      <motion.div
        animate={{
          y: [0, -15, 0],
          x: [0, 10, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-20 left-10 w-16 h-16 rounded-full bg-green-200 opacity-30 blur-xl"
      ></motion.div>

      <motion.div
        animate={{
          y: [0, 20, 0],
          x: [0, -15, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
        className="absolute bottom-10 right-10 w-24 h-24 rounded-full bg-blue-200 opacity-30 blur-xl"
      ></motion.div>

      <div className=" w-full  left-0 right-0 mt-4 bg-gradient-to-l  from-blue-gray-700 to-transparent">
        <div className="flex items-end justify-end px-10">
          <div className="flex flex-row items-center gap-1">
            <div className="text-md text-gray-800"><LanguagesIcon size={20} /></div>
            <div className="text-gray-800">
              <ReactFlagsSelect
                selected={country}
                onSelect={(code) => setSelectedLanguage(code)}
                countries={['US', 'LK', 'DE']}
                customLabels={{ US: 'English', LK: 'Sinhala', DE: 'Deutsch' }}
                showSelectedLabel={true}
                selectedSize={15}
                optionsSize={15}
                className="flag-select text-gray-800 "
                selectButtonClassName="!p-0 !border-0 !outline-none focus:!ring-0 focus:!ring-offset-0 !bg-transparent !text-gray-800"
              />
            </div>
          </div>
        </div>
      </div>



      <div className="container mx-auto px-4 py-2 md:py-24 flex flex-col items-center">
        {/* Main heading with animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mb-8"
        >





          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 Blinker-SemiBold">
            Exceptional Healthcare <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-600">For You</span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-lg md:text-xl text-gray-600 leading-relaxed Blinker-Light"
          >
            {description}
          </motion.p>
        </motion.div>

        {/* CTA Button with enhanced animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mb-6 flex flex-col items-center gap-6"
        >
          <Link to={"/book-appointment"} className="w-full max-w-xs">
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 25px -5px rgba(16, 185, 129, 0.4)"
              }}
              whileTap={{ scale: 0.98 }}
              className="relative overflow-hidden w-full bg-gradient-to-r from-green-500 to-green-700 font-bold text-white text-lg md:text-xl py-4 px-8 rounded-xl shadow-lg transition-all duration-300"
            >
              {/* Shine effect */}
              <span className="absolute top-0 left-0 w-full h-full bg-white opacity-0 hover:opacity-10 transition-opacity duration-300"></span>

              {/* Button text */}
              <span className="relative z-10 flex items-center justify-center gap-3 Blinker-SemiBold">
                Book an Appointment
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>

              {/* Pulse animation */}
              <motion.span
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0, 0.5],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 border-2 border-white/50 rounded-xl pointer-events-none"
              ></motion.span>
            </motion.button>
          </Link>

          {/* Secondary CTA */}

        </motion.div>


      </div>
    </div>
  );
};

export default MainCarousel;